import { relations } from 'drizzle-orm';
import {
  type AnySQLiteColumn,
  index,
  integer,
  real,
  sqliteTable,
  text,
  unique
} from 'drizzle-orm/sqlite-core';

import { baseColumns } from './baseColumns';

export type SourceType = 'client' | 'mobile';

export const privatePosts = sqliteTable(
  'private_posts',
  {
    ...baseColumns,
    content: text('content').notNull(),
    anonymous: integer('anonymous', { mode: 'boolean' }).notNull(),
    spaceId: integer('space_id'),
    userId: integer('user_id').notNull(),
    parentPostId: text('parent_post_id').references(
      (): AnySQLiteColumn => privatePosts.id,
      { onDelete: 'cascade' }
    ),
    slug: text('slug')
  },
  (t) => [
    index('idx_private_posts_created_at').on(t.createdAt),
    index('private_posts_parent_created').on(t.parentPostId, t.createdAt)
  ]
);

export const privateUploads = sqliteTable(
  'private_uploads',
  {
    ...baseColumns,
    postId: text('post_id').references(() => privatePosts.id, {
      onDelete: 'set null'
    }),
    key: text('key').notNull(), // Blob storage key
    type: text('type').notNull() // 'IMAGE' | 'VIDEO' | 'DOCUMENT'
  },
  (t) => [
    index('idx_private_uploads_post_id').on(t.postId),
    index('idx_private_uploads_key').on(t.key)
  ]
);

export const privatePostFactiiiCounts = sqliteTable(
  'private_post_factiii_counts',
  {
    ...baseColumns,
    postId: text('post_id')
      .notNull()
      .references(() => privatePosts.id, { onDelete: 'cascade' }),
    factiiiId: integer('factiii_id').notNull(),
    count: integer('count').notNull()
  },
  (t) => [
    index('factiii_counts_post_factiii').on(t.postId, t.factiiiId),
    unique('factiii_counts_unique_factiii_post').on(t.factiiiId, t.postId)
  ]
);

export const privatePostFactiiis = sqliteTable(
  'private_post_factiiis',
  {
    ...baseColumns,
    postFactiiiCountId: text('post_factiii_count_id').references(
      () => privatePostFactiiiCounts.id,
      { onDelete: 'cascade' }
    ),
    anonymous: integer('anonymous', { mode: 'boolean' }).notNull(),
    data: text('data'),
    number: real('number') // INTEGER_REQUIRED's value
  },
  (t) => [index('idx_ppf_count_id').on(t.postFactiiiCountId)]
);

export const privatePostFactiiiUploads = sqliteTable(
  'private_post_factiii_uploads',
  {
    ...baseColumns,
    postFactiiiId: text('post_factiii_id')
      .notNull()
      .references(() => privatePostFactiiis.id, { onDelete: 'cascade' }),
    uploadId: text('upload_id')
      .notNull()
      .references(() => privateUploads.id, { onDelete: 'cascade' })
  },
  (t) => [index('idx_ppfu_post_factiii_id').on(t.postFactiiiId)]
);

// Post references — links a PostFactiii to referenced Posts (server or private)
export const privatePostFactiiiPostReferences = sqliteTable(
  'private_post_factiii_post_references',
  {
    ...baseColumns,
    postFactiiiId: text('post_factiii_id')
      .notNull()
      .references(() => privatePostFactiiis.id, { onDelete: 'cascade' }),
    // UUID of the referenced post — prefixed with "private_" for local posts
    postUuid: text('post_uuid').notNull()
  },
  (t) => [index('idx_ppfpr_post_factiii_id').on(t.postFactiiiId)]
);

export const privatePostFactiiiReferences = sqliteTable(
  'private_post_factiii_references',
  {
    ...baseColumns,
    factiiiId: integer('factiii_id').notNull(),
    // The PostFactiii making the reference
    sourcePostFactiiiId: text('source_post_factiii_id')
      .notNull()
      .references(() => privatePostFactiiis.id, { onDelete: 'cascade' }),
    // The PostFactiii being referenced
    targetPostFactiiiId: text('target_post_factiii_id')
      .notNull()
      .references(() => privatePostFactiiis.id, { onDelete: 'cascade' })
  },
  (t) => [
    index('idx_ppfr_source_id').on(t.sourcePostFactiiiId),
    index('idx_ppfr_target_id').on(t.targetPostFactiiiId),
    unique('ppfr_unique_target').on(t.targetPostFactiiiId)
  ]
);

export const spaceTimes = sqliteTable(
  'space_times',
  {
    ...baseColumns,
    postFactiiiId: text('post_factiii_id')
      .notNull()
      .references(() => privatePostFactiiis.id, { onDelete: 'cascade' }),
    timestamp: integer('timestamp'), // Unix timestamp (ms)
    latitude: real('latitude'),
    longitude: real('longitude'),
    display: text('display'),
    altitude: integer('altitude')
  },
  (t) => [index('idx_space_times_post_factiii_id').on(t.postFactiiiId)]
);

export const privatePostsRelations = relations(
  privatePosts,
  ({ one, many }) => ({
    parent: one(privatePosts, {
      fields: [privatePosts.parentPostId],
      references: [privatePosts.id],
      relationName: 'postReplies'
    }),
    replies: many(privatePosts, { relationName: 'postReplies' }),
    uploads: many(privateUploads),
    factiiiCounts: many(privatePostFactiiiCounts)
  })
);

export const privateUploadsRelations = relations(
  privateUploads,
  ({ one, many }) => ({
    post: one(privatePosts, {
      fields: [privateUploads.postId],
      references: [privatePosts.id]
    }),
    factiiiUploadJunctions: many(privatePostFactiiiUploads)
  })
);

export const privatePostFactiiiCountsRelations = relations(
  privatePostFactiiiCounts,
  ({ one, many }) => ({
    post: one(privatePosts, {
      fields: [privatePostFactiiiCounts.postId],
      references: [privatePosts.id]
    }),
    factiiis: many(privatePostFactiiis)
  })
);

export const privatePostFactiiisRelations = relations(
  privatePostFactiiis,
  ({ one, many }) => ({
    factiiiCount: one(privatePostFactiiiCounts, {
      fields: [privatePostFactiiis.postFactiiiCountId],
      references: [privatePostFactiiiCounts.id]
    }),
    uploadJunctions: many(privatePostFactiiiUploads),
    spaceTimes: many(spaceTimes),
    postReferences: many(privatePostFactiiiPostReferences),
    // References where this factiii is the source (has many references)
    factiiiReferences: many(privatePostFactiiiReferences, {
      relationName: 'factiiiReferencesSource'
    }),
    // Reference where this factiii is the target (referenced by one)
    referencedBy: many(privatePostFactiiiReferences, {
      relationName: 'factiiiReferencesTarget'
    })
  })
);

export const privatePostFactiiiReferencesRelations = relations(
  privatePostFactiiiReferences,
  ({ one }) => ({
    sourceFactiii: one(privatePostFactiiis, {
      fields: [privatePostFactiiiReferences.sourcePostFactiiiId],
      references: [privatePostFactiiis.id],
      relationName: 'factiiiReferencesSource'
    }),
    targetFactiii: one(privatePostFactiiis, {
      fields: [privatePostFactiiiReferences.targetPostFactiiiId],
      references: [privatePostFactiiis.id],
      relationName: 'factiiiReferencesTarget'
    })
  })
);

export const privatePostFactiiiPostReferencesRelations = relations(
  privatePostFactiiiPostReferences,
  ({ one }) => ({
    postFactiii: one(privatePostFactiiis, {
      fields: [privatePostFactiiiPostReferences.postFactiiiId],
      references: [privatePostFactiiis.id]
    })
  })
);

export const privatePostFactiiiUploadsRelations = relations(
  privatePostFactiiiUploads,
  ({ one }) => ({
    factiii: one(privatePostFactiiis, {
      fields: [privatePostFactiiiUploads.postFactiiiId],
      references: [privatePostFactiiis.id]
    }),
    upload: one(privateUploads, {
      fields: [privatePostFactiiiUploads.uploadId],
      references: [privateUploads.id]
    })
  })
);

export const spaceTimesRelations = relations(spaceTimes, ({ one }) => ({
  factiii: one(privatePostFactiiis, {
    fields: [spaceTimes.postFactiiiId],
    references: [privatePostFactiiis.id]
  })
}));
